import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import ForkRight from '@mui/icons-material/ForkRight';
import Star from '@mui/icons-material/Star';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ChapterVariantOption {
  id: string;
  name: string;
  notes?: string;
  updatedAt?: string;
  isMain: boolean;
}

export interface ChapterSceneVariantOption {
  sceneId: string;
  sceneName: string;
  selectedContentId: string;
  mainContentId?: string;
  canMoveToNewVariant: boolean;
  synchronized: boolean;
  variants: Array<{ id: string; name: string }>;
}

interface Props {
  open: boolean;
  variants: ChapterVariantOption[];
  selectedId: string;
  disabled: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  sceneVariants: ChapterSceneVariantOption[];
  onCreate: (name: string, selections: Record<string, string>) => Promise<void>;
  onMoveSceneToNewVariant: (sceneId: string) => Promise<void>;
  onPromote: () => Promise<void>;
  promotingPublicChapter: boolean;
  onUpdateMetadata: (name: string, notes: string) => Promise<void>;
}

export function ChapterVariantsDrawer(props: Props) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [sceneSelections, setSceneSelections] = useState<Record<string, string>>({});
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const selected = props.variants.find((item) => item.id === props.selectedId);
  const [name, setName] = useState(selected?.name || '');
  const [notes, setNotes] = useState(selected?.notes || '');

  useEffect(() => {
    setName(selected?.name || '');
    setNotes(selected?.notes || '');
  }, [selected?.id, selected?.name, selected?.notes]);

  const ordered = useMemo(() => [...props.variants].sort((a, b) => {
    if (a.isMain !== b.isMain) return a.isMain ? -1 : 1;
    return (b.updatedAt || '').localeCompare(a.updatedAt || '');
  }), [props.variants]);

  const create = async () => {
    setIsCreating(true);
    try {
      await props.onCreate(newName.trim(), sceneSelections);
      setNewName('');
      setCreateOpen(false);
    } finally {
      setIsCreating(false);
    }
  };

  const promote = async () => {
    setIsPromoting(true);
    try {
      await props.onPromote();
      setPromoteOpen(false);
    } finally {
      setIsPromoting(false);
    }
  };

  return <>
    <Drawer anchor={'right'} open={props.open} onClose={props.onClose} variant={'temporary'}>
      <Stack sx={{ width: { xs: '100vw', sm: 380 }, height: '100%' }}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: 2 }}>
          <Typography variant={'h6'}>{t('chapters.publish.variants_title')}</Typography>
          <IconButton onClick={props.onClose}><Close /></IconButton>
        </Stack>
        <Divider />
        <Stack spacing={2} sx={{ p: 2 }}>
          <TextField label={t('chapters.publish.variant_name')} value={name} onChange={(event) => setName(event.target.value)} disabled={props.disabled} />
          <TextField label={t('chapters.publish.variant_notes')} value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={4} disabled={props.disabled} />
          <Typography variant={'subtitle2'}>{t('chapters.publish.variant_scenes')}</Typography>
          {props.sceneVariants.map((scene) => {
            const usesMainFromNonMainChapter = !selected?.isMain && scene.selectedContentId === scene.mainContentId;
            return <Stack key={scene.sceneId} direction={'row'} alignItems={'center'} spacing={1}>
              <ListItemText
                primary={scene.sceneName}
                secondary={<><Typography component={'span'} variant={'body2'} color={usesMainFromNonMainChapter ? 'warning.main' : 'text.secondary'}>{scene.variants.find((variant) => variant.id === scene.selectedContentId)?.name || 'Default'}</Typography>{` · ${scene.synchronized ? t('chapters.publish.variant_synced') : t('chapters.publish.variant_out_of_sync')}`}</>}
              />
              {usesMainFromNonMainChapter && <Tooltip title={t('chapters.publish.variant_main_scene_warning')}><WarningAmber color={'warning'} fontSize={'small'} /></Tooltip>}
              {scene.canMoveToNewVariant && <Tooltip title={t('chapters.publish.variant_move_scene_new')}>
                <IconButton size={'small'} disabled={props.disabled} onClick={() => props.onMoveSceneToNewVariant(scene.sceneId)}><ForkRight fontSize={'small'} /></IconButton>
              </Tooltip>}
            </Stack>;
          })}
          <Stack direction={'row'} spacing={1}>
            <Button variant={'contained'} disabled={props.disabled || !name.trim()} onClick={() => props.onUpdateMetadata(name.trim(), notes)}>{t('common.save')}</Button>
            {!selected?.isMain && <Button variant={'outlined'} disabled={props.disabled} onClick={() => setPromoteOpen(true)}>{t('chapters.publish.variant_promote')}</Button>}
          </Stack>
        </Stack>
        <Divider />
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ px: 2, pt: 2 }}>
          <Typography variant={'subtitle2'} color={'text.secondary'}>{t('chapters.publish.variants_list')}</Typography>
          <IconButton size={'small'} onClick={() => setCreateOpen(true)} disabled={props.disabled} title={t('chapters.publish.variant_create')}><Add /></IconButton>
        </Stack>
        <List sx={{ overflowY: 'auto' }}>
          {ordered.map((variant) => <ListItemButton key={variant.id} selected={variant.id === props.selectedId} onClick={() => props.onSelect(variant.id)}>
            <ListItemText primary={variant.name} secondary={variant.isMain ? t('chapters.publish.variant_main') : variant.notes} />
            {variant.isMain && <Star color={'primary'} fontSize={'small'} />}
          </ListItemButton>)}
        </List>
      </Stack>
    </Drawer>
    <Dialog open={createOpen} onClose={isCreating ? undefined : () => setCreateOpen(false)} maxWidth={'xs'} fullWidth disableEscapeKeyDown={isCreating}>
      <DialogTitle>{t('chapters.publish.variant_create')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField autoFocus fullWidth label={t('chapters.publish.variant_name')} value={newName} onChange={(event) => setNewName(event.target.value)} />
          {props.sceneVariants.map((scene) => <TextField
            key={scene.sceneId}
            select
            fullWidth
            label={scene.sceneName}
            value={sceneSelections[scene.sceneId] || scene.selectedContentId}
            onChange={(event) => setSceneSelections((current) => ({ ...current, [scene.sceneId]: event.target.value }))}
          >
            {scene.variants.length === 1 && <MenuItem value={scene.variants[0].id}>{t('chapters.publish.variant_default')}</MenuItem>}
            {scene.variants.length > 1 && scene.variants.map((variant) => <MenuItem key={variant.id} value={variant.id}>{variant.name}</MenuItem>)}
            <MenuItem value={'new'}>{t('chapters.publish.variant_new_dedicated')}</MenuItem>
          </TextField>)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateOpen(false)} disabled={isCreating}>{t('common.cancel')}</Button>
        <Button variant={'contained'} disabled={!newName.trim() || isCreating} onClick={create} startIcon={isCreating ? <CircularProgress size={16} color={'inherit'} /> : undefined}>
          {isCreating ? t('chapters.publish.variant_creating') : t('editor.create')}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={promoteOpen} onClose={isPromoting ? undefined : () => setPromoteOpen(false)} maxWidth={'xs'} fullWidth disableEscapeKeyDown={isPromoting}>
      <DialogTitle>{t('chapters.publish.variant_promote_confirm_title')}</DialogTitle>
      <DialogContent>
        <Typography>
          {props.promotingPublicChapter
            ? t('chapters.publish.variant_promote_public_warning')
            : t('chapters.publish.variant_promote_confirm')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPromoteOpen(false)} disabled={isPromoting}>{t('common.cancel')}</Button>
        <Button variant={'contained'} color={props.promotingPublicChapter ? 'warning' : 'primary'} disabled={isPromoting} onClick={promote} startIcon={isPromoting ? <CircularProgress size={16} color={'inherit'} /> : undefined}>
          {isPromoting ? t('chapters.publish.variant_promoting') : t('chapters.publish.variant_promote_confirm_action')}
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}
